import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { getUser, clearAuth } from '../../utils/auth';
import { toast } from 'sonner';
import { 
  Users, FileText, Plane, Clock, Star, CheckCircle, XCircle, 
  AlertCircle, Calendar, Phone, Mail, MessageSquare, Upload,
  Hotel, Car, FileCheck, TrendingUp, Activity, Menu, Bell,
  LogOut, Home, ClipboardList, GitBranch, Settings, User,
  ArrowRight, Building2, Stethoscope, ChevronRight, Edit,
  Check, X, StickyNote, Eye, Filter, Search, BarChart3,
  MapPin, DollarSign, Percent, ChevronDown, ChevronUp
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import ThemeToggle from '../../components/ThemeToggle';
import NotificationBell from '../../components/NotificationBell';
import CaseCopilotDialog from '../../components/CaseCopilotDialog';
import { Sparkles } from 'lucide-react';

export default function CaseManagerDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('tracker');
  const [selectedCase, setSelectedCase] = useState(null);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showPatientDialog, setShowPatientDialog] = useState(false);
  const [showCopilot, setShowCopilot] = useState(false);
  const [copilotCase, setCopilotCase] = useState(null);
  const [currentStageForNotes, setCurrentStageForNotes] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedMenus, setExpandedMenus] = useState({ patientManagement: true });
  
  // Pipeline configuration from Bigin
  const [pipelineConfig, setPipelineConfig] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);

  // Icon mapping for stages
  const stageIconMap = {
    'Patient Query': Mail,
    'Inquiry': Mail,
    'Gather Requirements': FileCheck,
    'Requirements Assesment': FileCheck,
    'Patient Onboarding': Users,
    'Medical Assesment': Stethoscope,
    'Patient Quotation Sent': FileText,
    'Hospital Selection': Building2,
    'Patient Approved': CheckCircle,
    'Hospital and Patient Approval': CheckCircle,
    'Patient Visa and Documentation Begin': FileText,
    'Visa and Documents': FileText,
    'Visa And Travel Arrangements': Plane,
    'Travel and Logistics': Plane,
    'Hospital Approval': Building2,
    'Pre Arrival Preparation': Calendar,
    'Dates Finalised / Treatment Scheduled': Calendar,
    'Arrival - Departure': Plane,
    'Completed': CheckCircle,
    'Cancelled': XCircle,
    'default': Activity
  };

  // Get icon for a stage name
  const getStageIcon = (stageName) => {
    return stageIconMap[stageName] || stageIconMap['default'];
  };

  // Build journey stages from pipeline config
  const patientJourneyStages = pipelineConfig?.stages?.map((stage, idx) => ({
    id: stage.id || idx + 1,
    name: stage.name,
    shortName: stage.name.split(' ').slice(0, 2).join(' '),
    bigin_id: stage.bigin_id,
    sequence: stage.sequence || idx + 1,
    owner: 'case_manager',
    icon: getStageIcon(stage.name)
  })) || [
    // Default fallback stages (Bigin Customer Onboarding)
    { id: 1, name: 'Patient Query', shortName: 'Query', owner: 'case_manager', icon: Mail },
    { id: 2, name: 'Gather Requirements', shortName: 'Requirements', owner: 'case_manager', icon: FileCheck },
    { id: 3, name: 'Patient Onboarding', shortName: 'Onboarding', owner: 'case_manager', icon: Users },
    { id: 4, name: 'Patient Quotation Sent', shortName: 'Quotation', owner: 'case_manager', icon: FileText },
    { id: 5, name: 'Patient Approved', shortName: 'Approved', owner: 'case_manager', icon: CheckCircle },
    { id: 6, name: 'Visa and Documentation', shortName: 'Visa Docs', owner: 'case_manager', icon: FileText },
    { id: 7, name: 'Travel Arrangements', shortName: 'Travel', owner: 'case_manager', icon: Plane },
    { id: 8, name: 'Hospital Approval', shortName: 'Hospital', owner: 'case_manager', icon: Building2 },
    { id: 9, name: 'Treatment Scheduled', shortName: 'Scheduled', owner: 'case_manager', icon: Calendar },
    { id: 10, name: 'Arrival - Departure', shortName: 'Arrival', owner: 'case_manager', icon: Plane },
    { id: 11, name: 'Completed', shortName: 'Completed', owner: 'case_manager', icon: CheckCircle },
    { id: 12, name: 'Cancelled', shortName: 'Cancelled', owner: 'case_manager', icon: XCircle }
  ];

  const menuStructure = [
    {
      id: 'overview',
      label: 'Overview',
      icon: Home,
      children: [
        { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
        { id: 'analytics', label: 'Analytics', icon: TrendingUp }
      ]
    },
    {
      id: 'patientManagement',
      label: 'Patient Management',
      icon: Users,
      children: [
        { id: 'tracker', label: 'Journey Tracker', icon: ClipboardList },
        { id: 'patients', label: 'All Patients', icon: Users }
      ]
    },
    {
      id: 'journeyManagement',
      label: 'Journey',
      icon: GitBranch,
      children: [
        { id: 'stages', label: 'Stage Flowchart', icon: GitBranch }
      ]
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      isStandalone: true,
      section: 'settings'
    }
  ];

  useEffect(() => {
    loadPipelineConfig();
    loadDashboardData();
  }, []);

  const loadPipelineConfig = async () => {
    setPipelineLoading(true);
    try {
      const response = await api.get('/case_manager/pipeline-config');
      setPipelineConfig(response.data);
    } catch (error) {
      // Will use default stages
    } finally {
      setPipelineLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const [statsRes, casesRes] = await Promise.all([
        api.get('/case_manager/dashboard-stats'),
        api.get('/case_manager/cases')
      ]);

      setStats(statsRes.data.stats);
      
      const casesWithStages = casesRes.data.cases.map(caseItem => ({
        ...caseItem,
        stageData: initializeStageData(caseItem.current_stage_id || 1),
        notes: caseItem.notes || {}
      }));
      
      setCases(casesWithStages);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const initializeStageData = (currentStageId) => {
    const stageData = {};
    patientJourneyStages.forEach(stage => {
      stageData[stage.id] = {
        completed: stage.id < currentStageId,
        inProgress: stage.id === currentStageId,
        pending: stage.id > currentStageId
      };
    });
    return stageData;
  };

  const handleMarkDone = async (caseId, stageId) => {
    try {
      // Call backend API to update stage
      const response = await api.patch(`/case_manager/cases/${caseId}/stage/${stageId}`, {
        completed: true
      });
      
      // Update local state with backend response
      setCases(prevCases => 
        prevCases.map(c => {
          if (c._id === caseId) {
            const newStageData = { ...c.stageData };
            newStageData[stageId].completed = true;
            newStageData[stageId].inProgress = false;
            
            if (newStageData[stageId + 1]) {
              newStageData[stageId + 1].inProgress = true;
              newStageData[stageId + 1].pending = false;
            }
            
            return {
              ...c,
              current_stage_id: response.data.current_stage_id || (stageId < 16 ? stageId + 1 : stageId),
              stageData: newStageData
            };
          }
          return c;
        })
      );
      
      toast.success(`Stage ${stageId} marked as complete!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const handleOpenNotes = (caseItem, stageId) => {
    setSelectedCase(caseItem);
    setCurrentStageForNotes(stageId);
    setNoteText(caseItem.notes?.[stageId] || '');
    setShowNotesDialog(true);
  };

  const handleSaveNotes = async () => {
    try {
      // Call backend API to save note
      await api.post(`/case_manager/cases/${selectedCase._id}/stage/${currentStageForNotes}/notes`, {
        notes: noteText
      });
      
      // Update local state
      setCases(prevCases => 
        prevCases.map(c => {
          if (c._id === selectedCase._id) {
            return {
              ...c,
              notes: {
                ...c.notes,
                [currentStageForNotes]: noteText
              }
            };
          }
          return c;
        })
      );
      
      toast.success('Note saved successfully!');
      setShowNotesDialog(false);
      setNoteText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save note');
    }
  };

  const handleViewPatient = (caseItem) => {
    setSelectedCase(caseItem);
    setShowPatientDialog(true);
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const getStageProgress = (stageId) => {
    return Math.min((stageId / 16) * 100, 100);
  };

  const filteredCases = cases.filter(caseItem => {
    const matchesSearch = caseItem.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         caseItem.treatment_type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || caseItem.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950" data-portal="case_manager">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-portal="case_manager">
      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 lg:hidden safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {[
            { icon: ClipboardList, label: 'Tracker', section: 'tracker' },
            { icon: Users, label: 'Patients', section: 'patients' },
            { icon: CheckCircle, label: 'Tasks', section: 'dashboard' },
            { icon: FileText, label: 'Docs', section: 'stages' },
            { icon: User, label: 'Profile', section: 'settings' },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.section;
            return (
              <button
                key={item.section}
                onClick={() => setActiveSection(item.section)}
                className={`flex flex-col items-center justify-center flex-1 h-full px-1 py-2 transition-all ${
                  isActive ? 'bg-amber-50 dark:bg-amber-900/20' : ''
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}`} strokeWidth={1.5} />
                <span className={`text-[10px] font-medium mt-0.5 ${isActive ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800 z-40 h-16">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Menu className="w-5 h-5 text-gray-700" />
            </button>
            
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <ClipboardList className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-base font-bold text-gray-900">MedRahi CM</h1>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <NotificationBell role="case_manager" />

            <div className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded-lg cursor-pointer">
              <div className="text-right hidden md:block">
                <p className="text-xs font-semibold text-gray-900">{user.full_name}</p>
              </div>
              <div className="w-7 h-7 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                {(user.full_name || 'C')[0].toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex pt-14">
        {/* Compact Sidebar with Dropdowns */}
        <aside
          className={`fixed left-0 top-14 bottom-0 bg-white border-r border-gray-200 transition-all duration-300 z-30 ${
            sidebarOpen ? 'w-56' : 'w-0'
          } overflow-hidden overflow-y-auto`}
        >
          <nav className="p-3 space-y-1">
            {menuStructure.map((menu) => {
              const Icon = menu.icon;
              
              if (menu.isStandalone) {
                const isActive = activeSection === menu.section;
                return (
                  <button
                    key={menu.id}
                    onClick={() => setActiveSection(menu.section)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm ${
                      isActive
                        ? 'bg-orange-50 text-orange-600 font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid={`menu-${menu.section}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{menu.label}</span>
                  </button>
                );
              }
              
              const isExpanded = expandedMenus[menu.id];
              
              return (
                <div key={menu.id}>
                  <button
                    onClick={() => toggleMenu(menu.id)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-50 transition-all text-sm font-medium"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span>{menu.label}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="mt-1 space-y-1 ml-4">
                      {menu.children.map((child) => {
                        const ChildIcon = child.icon;
                        const isActive = activeSection === child.id;
                        return (
                          <button
                            key={child.id}
                            onClick={() => setActiveSection(child.id)}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                              isActive
                                ? 'bg-orange-50 text-orange-600 font-semibold'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                            data-testid={`menu-${child.id}`}
                          >
                            <ChildIcon className="w-3.5 h-3.5" />
                            <span>{child.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="pt-3 mt-3 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-all text-sm"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-56' : 'ml-0'}`}>
          <div className="p-4">
            {/* Patient Tracker */}
            {activeSection === 'tracker' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-heading font-bold text-gray-900">Patient Journey Tracker</h2>
                  <p className="text-sm text-gray-600 mt-1">Track patient progress through every stage</p>
                </div>

                <div className="mb-4 flex items-center gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search patients..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="all">All Status</option>
                    <option value="new">New</option>
                    <option value="in_india">In India</option>
                    <option value="treatment_ongoing">Treatment</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-green-100 border-2 border-green-500 rounded"></div>
                    <span className="text-gray-700">Done</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-orange-100 border-2 border-orange-500 rounded"></div>
                    <span className="text-gray-700">In Progress</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-gray-100 border-2 border-gray-300 rounded"></div>
                    <span className="text-gray-700">Pending</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-emerald-100 border-2 border-emerald-500 rounded"></div>
                    <span className="text-gray-700">Hospital</span>
                  </div>
                </div>

                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b-2 border-gray-200">
                          <tr>
                            <th className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left text-xs font-bold text-gray-900 min-w-[60px]">Sr</th>
                            <th className="sticky left-[60px] z-20 bg-gray-50 px-3 py-2 text-left text-xs font-bold text-gray-900 min-w-[180px]">Patient</th>
                            {patientJourneyStages.map(stage => (
                              <th 
                                key={stage.id} 
                                className={`px-3 py-2 text-center text-xs font-bold min-w-[120px] ${
                                  stage.owner === 'hospital' ? 'bg-emerald-50 text-emerald-900' : 'text-gray-900'
                                }`}
                              >
                                <div className="flex flex-col items-center gap-0.5">
                                  <span className="text-xs font-mono">{stage.id}</span>
                                  <span className="font-semibold text-xs">{stage.shortName}</span>
                                  {stage.owner === 'hospital' && (
                                    <Badge className="bg-emerald-600 text-white text-[9px] px-1 py-0">H</Badge>
                                  )}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCases.length === 0 ? (
                            <tr>
                              <td colSpan={18} className="text-center py-8">
                                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                <p className="text-sm font-semibold text-gray-900 mb-1">No Patients Found</p>
                                <p className="text-xs text-gray-600">Adjust filters</p>
                              </td>
                            </tr>
                          ) : (
                            filteredCases.map((caseItem, index) => (
                              <tr key={caseItem._id || index} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="sticky left-0 z-10 bg-white px-3 py-3 text-xs font-semibold text-gray-900">
                                  {index + 1}
                                </td>
                                <td className="sticky left-[60px] z-10 bg-white px-3 py-3">
                                  <div>
                                    <p className="font-semibold text-xs text-gray-900">{caseItem.patient_name}</p>
                                    <p className="text-[10px] text-gray-500">{caseItem.treatment_type}</p>
                                  </div>
                                </td>
                                {patientJourneyStages.map(stage => {
                                  const stageData = caseItem.stageData?.[stage.id] || {};
                                  const isCompleted = stageData.completed;
                                  const isInProgress = stageData.inProgress;
                                  const isPending = stageData.pending;
                                  const hasNote = caseItem.notes?.[stage.id];
                                  const isHospitalStage = stage.owner === 'hospital';
                                  
                                  return (
                                    <td 
                                      key={stage.id} 
                                      className={`px-2 py-2 text-center ${isHospitalStage ? 'bg-emerald-50' : ''}`}
                                    >
                                      <div className={`rounded-lg p-1.5 border-2 ${
                                        isCompleted ? 'bg-green-50 border-green-500' :
                                        isInProgress ? 'bg-orange-50 border-orange-500' :
                                        'bg-gray-50 border-gray-300'
                                      }`}>
                                        {isCompleted && (
                                          <div className="flex flex-col items-center gap-0.5">
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                            <span className="text-[9px] text-green-700 font-semibold">Done</span>
                                          </div>
                                        )}
                                        
                                        {isInProgress && (
                                          <div className="flex flex-col items-center gap-1">
                                            <Clock className="w-4 h-4 text-orange-600" />
                                            <button
                                              onClick={() => handleMarkDone(caseItem._id, stage.id)}
                                              className="text-[9px] bg-orange-600 text-white px-1.5 py-0.5 rounded hover:bg-orange-700 font-semibold"
                                            >
                                              Mark Done
                                            </button>
                                          </div>
                                        )}
                                        
                                        {isPending && (
                                          <div className="flex flex-col items-center gap-0.5">
                                            <XCircle className="w-4 h-4 text-gray-400" />
                                            <span className="text-[9px] text-gray-500">Pending</span>
                                          </div>
                                        )}
                                        
                                        <button
                                          onClick={() => handleOpenNotes(caseItem, stage.id)}
                                          className={`mt-1 text-[9px] px-1.5 py-0.5 rounded transition-colors flex items-center gap-0.5 mx-auto ${
                                            hasNote ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' :
                                            'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                          }`}
                                        >
                                          <StickyNote className="w-2.5 h-2.5" />
                                          {hasNote ? 'View' : 'Add'}
                                        </button>
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                <div className="mt-4 grid grid-cols-4 gap-3">
                  <Card>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600">Total</p>
                          <p className="text-xl font-bold text-gray-900">{cases.length}</p>
                        </div>
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600">In Treatment</p>
                          <p className="text-xl font-bold text-gray-900">{cases.filter(c => c.current_stage_id === 13).length}</p>
                        </div>
                        <Stethoscope className="w-6 h-6 text-green-600" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600">Completed</p>
                          <p className="text-xl font-bold text-gray-900">{cases.filter(c => c.current_stage_id === 16).length}</p>
                        </div>
                        <CheckCircle className="w-6 h-6 text-purple-600" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600">Progress</p>
                          <p className="text-xl font-bold text-gray-900">
                            {Math.round(cases.reduce((sum, c) => sum + ((c.current_stage_id || 1) / 16 * 100), 0) / (cases.length || 1))}%
                          </p>
                        </div>
                        <TrendingUp className="w-6 h-6 text-orange-600" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Dashboard */}
            {activeSection === 'dashboard' && (
              <div>
                <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">Dashboard</h2>
                
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600">Active</p>
                          <p className="text-2xl font-bold text-gray-900">{cases.filter(c => c.is_active).length}</p>
                        </div>
                        <Users className="w-8 h-8 text-blue-600" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600">In Treatment</p>
                          <p className="text-2xl font-bold text-gray-900">{cases.filter(c => c.current_stage_id === 13).length}</p>
                        </div>
                        <Stethoscope className="w-8 h-8 text-green-600" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600">Completed</p>
                          <p className="text-2xl font-bold text-gray-900">{cases.filter(c => c.status === 'completed').length}</p>
                        </div>
                        <CheckCircle className="w-8 h-8 text-purple-600" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600">Success</p>
                          <p className="text-2xl font-bold text-gray-900">93%</p>
                        </div>
                        <TrendingUp className="w-8 h-8 text-orange-600" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Cases</h3>
                    <div className="space-y-2">
                      {cases.slice(0, 5).map((caseItem) => (
                        <div key={caseItem._id} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm text-gray-900">{caseItem.patient_name}</h4>
                              <p className="text-xs text-gray-600 mt-0.5">{caseItem.treatment_type}</p>
                            </div>
                            <Button
                              onClick={() => handleViewPatient(caseItem)}
                              variant="outline"
                              className="text-xs h-7"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* All Patients */}
            {activeSection === 'patients' && (
              <div>
                <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">All Patients</h2>
                
                <div className="grid gap-3">
                  {cases.map((caseItem) => (
                    <Card key={caseItem._id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                {(caseItem.patient_name || 'P')[0].toUpperCase()}
                              </div>
                              <div>
                                <h3 className="text-sm font-semibold text-gray-900">{caseItem.patient_name}</h3>
                                <p className="text-xs text-gray-600">{caseItem.patient_country}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-4 gap-3 mt-3">
                              <div>
                                <p className="text-[10px] text-gray-500 mb-0.5">Treatment</p>
                                <p className="text-xs font-semibold text-gray-900">{caseItem.treatment_type}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-500 mb-0.5">Stage</p>
                                <p className="text-xs font-semibold text-gray-900">
                                  {caseItem.current_stage_id}/16
                                </p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-500 mb-0.5">Progress</p>
                                <div className="flex items-center gap-1">
                                  <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                    <div 
                                      className="bg-orange-600 h-1.5 rounded-full" 
                                      style={{ width: `${getStageProgress(caseItem.current_stage_id)}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs font-semibold text-gray-900">
                                    {Math.round(getStageProgress(caseItem.current_stage_id))}%
                                  </span>
                                </div>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-500 mb-0.5">Status</p>
                                <Badge className="bg-blue-100 text-blue-800 text-[10px]">
                                  {caseItem.status?.replace('_', ' ')}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => { setCopilotCase(caseItem); setShowCopilot(true); }}
                              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-xs h-8"
                            >
                              <Sparkles className="w-3 h-3 mr-1" />
                              AI Copilot
                            </Button>
                            <Button
                              onClick={() => handleViewPatient(caseItem)}
                              variant="outline"
                              className="text-xs h-8"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Journey Stages */}
            {activeSection === 'stages' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-heading font-bold text-gray-900">Journey Stages</h2>
                  <p className="text-sm text-gray-600 mt-1">16-stage patient journey flow</p>
                </div>

                <div className="flex items-center gap-4 mb-4 p-3 bg-white rounded-lg border border-gray-200 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-orange-500 rounded"></div>
                    <span className="text-gray-700">Case Manager (15)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span className="text-gray-700">Hospital (1)</span>
                  </div>
                </div>

                <div className="space-y-3">
                  {patientJourneyStages.map((stage, index) => {
                    const isHospitalStage = stage.owner === 'hospital';
                    const Icon = stage.icon;
                    
                    return (
                      <div key={stage.id}>
                        <Card className={isHospitalStage ? 'border-2 border-green-500 bg-green-50' : 'border-gray-200'}>
                          <CardContent className="p-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                                isHospitalStage ? 'bg-green-500' : 'bg-orange-500'
                              }`}>
                                <span className="text-white font-bold text-sm">{stage.id}</span>
                              </div>
                              
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <Icon className={`w-4 h-4 ${isHospitalStage ? 'text-green-700' : 'text-orange-700'}`} />
                                  <h3 className="text-sm font-semibold text-gray-900">{stage.name}</h3>
                                  {isHospitalStage && (
                                    <Badge className="bg-green-600 text-white text-[9px]">Hospital</Badge>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600">
                                  {isHospitalStage ? 'Medical treatment by hospital staff' : 'Managed by Case Manager'}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        
                        {index < patientJourneyStages.length - 1 && (
                          <div className="flex justify-center py-1">
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Analytics */}
            {activeSection === 'analytics' && (
              <div>
                <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">Analytics</h2>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">Stage Distribution</h3>
                        <BarChart3 className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="space-y-1.5">
                        {[1, 2, 3, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map(stageId => {
                          const count = cases.filter(c => c.current_stage_id === stageId).length;
                          if (count === 0) return null;
                          return (
                            <div key={stageId} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">Stage {stageId}</span>
                              <span className="font-semibold text-gray-900">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">Countries</h3>
                        <MapPin className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="space-y-1.5">
                        {[...new Set(cases.map(c => c.patient_country))].slice(0, 8).map(country => (
                          <div key={country} className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">{country}</span>
                            <span className="font-semibold text-gray-900">
                              {cases.filter(c => c.patient_country === country).length}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">Success Rate</h3>
                        <Percent className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="text-center py-3">
                        <p className="text-3xl font-bold text-gray-900">93%</p>
                        <p className="text-xs text-gray-600 mt-1">Cases completed</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Treatment Types</h3>
                    <div className="space-y-2">
                      {[...new Set(cases.map(c => c.treatment_type))].map(treatment => {
                        const count = cases.filter(c => c.treatment_type === treatment).length;
                        const percentage = Math.round((count / cases.length) * 100);
                        return (
                          <div key={treatment}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-700">{treatment}</span>
                              <span className="text-xs font-semibold text-gray-900">{count} ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div className="bg-orange-600 h-1.5 rounded-full" style={{ width: `${percentage}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Settings */}
            {activeSection === 'settings' && (
              <div>
                <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">Settings</h2>
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Profile</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                        <input type="text" value={user.full_name || ''} readOnly className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-gray-50" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                        <input type="email" value={user.email || ''} readOnly className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-gray-50" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base">
              {currentStageForNotes && `Stage ${currentStageForNotes}: ${patientJourneyStages.find(s => s.id === currentStageForNotes)?.name}`}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3">
            {selectedCase && (
              <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                <p className="text-sm font-semibold text-gray-900">{selectedCase.patient_name}</p>
                <p className="text-xs text-gray-600">{selectedCase.treatment_type}</p>
              </div>
            )}
            
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              placeholder="Enter notes..."
            />
            
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowNotesDialog(false)} className="text-sm h-8">
                Cancel
              </Button>
              <Button onClick={handleSaveNotes} className="bg-orange-600 hover:bg-orange-700 text-sm h-8">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Patient Dialog */}
      <Dialog open={showPatientDialog} onOpenChange={setShowPatientDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base">Patient Details</DialogTitle>
          </DialogHeader>
          {selectedCase && (
            <div className="mt-3 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {(selectedCase.patient_name || 'P')[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">{selectedCase.patient_name}</h3>
                  <p className="text-sm text-gray-600">{selectedCase.patient_country}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                    <span>{selectedCase.patient_email}</span>
                    <span>{selectedCase.patient_phone}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Treatment</p>
                  <p className="text-sm text-gray-900">{selectedCase.treatment_type}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Stage</p>
                  <p className="text-sm text-gray-900">Stage {selectedCase.current_stage_id}/16</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Budget</p>
                  <p className="text-sm text-gray-900">{selectedCase.budget_range}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Priority</p>
                  <Badge className="bg-orange-600 text-white text-xs">{selectedCase.priority}</Badge>
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">Condition</p>
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedCase.condition_description}</p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">Medical History</p>
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedCase.medical_history}</p>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setShowPatientDialog(false)} variant="outline" className="text-sm h-8">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Case Copilot */}
      <CaseCopilotDialog
        open={showCopilot}
        onOpenChange={setShowCopilot}
        caseItem={copilotCase}
      />
    </div>
  );
}
